import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@ui/card";
import { Skeleton } from "@ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ui/table";
import { useUserPlansCard } from "@/hooks/useUserPlansCard";
import { CURRENT_YEAR } from "@/utils/constants";
import { CreateNewPlanDialog } from "../molecules/CreateNewPlanDialog";
import { UserPlansListRow } from "../molecules/UserPlansListRow";

const loadingTableEntries = Array.from({ length: 1 }, (_, i) => i);

interface UserPlansCardProps {
  showCreateButton?: boolean;
}

export const UserPlansCard = ({
  showCreateButton = true,
}: UserPlansCardProps) => {
  const { t } = useTranslation();
  const {
    userPlans,
    isLoading,
    handlePlanClick,
    activeYear,
    getNumCollisionsForPlan,
    showCreateDialog,
    setShowCreateDialog,
  } = useUserPlansCard();

  const canCreatePlan = showCreateButton && activeYear >= CURRENT_YEAR;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t("your_plans")}</CardTitle>
          {canCreatePlan && (
            <Button
              size="sm"
              className="flex gap-x-1"
              onClick={() => setShowCreateDialog(true)}
            >
              <PlusIcon className="size-4" />{" "}
              <span className="hidden md:inline">Ny planering</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead className="hidden sm:table-cell">
                  {t("num_bookings")}
                </TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead>Krockar</TableHead>
                <TableHead className="hidden md:table-cell">
                  {t("updated")}
                </TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!isLoading ? (
                userPlans.length > 0 ? (
                  userPlans.map((plan) => (
                    <UserPlansListRow
                      key={plan.id}
                      plan={plan}
                      collisions={getNumCollisionsForPlan(plan.id)}
                      onPlanClick={() => handlePlanClick(plan, "edit")}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-muted-foreground"
                    >
                      {t("you_do_not_have_any_plans_yet")}
                    </TableCell>
                  </TableRow>
                )
              ) : (
                loadingTableEntries.map((index) => (
                  <TableRow key={`table-row-${index}`}>
                    <TableCell>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20" />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <CreateNewPlanDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </>
  );
};
